/********************************************************************
 * ADOBE CONFIDENTIAL
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import {breakpoint} from '../../breakpoints/breakpoint-macro' with {type: 'macro'};
import {clsx} from 'clsx';
import styles from './Skeleton.module.css';

export interface SkeletonWrapperProps {
	className?: string;
	children: string;
}

export interface SkeletonHeadingProps {
	lines?: number;
	level?: 1 | 2 | 3 | 4 | 5 | 6;
	align?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonRectProps {
	radius?: string | number;
	width?: string | number;
	height?: string | number;
	minWidth?: string | number;
	minHeight?: string | number;
	maxWidth?: string | number;
	maxHeight?: string | number;
}

export interface SkeletonParagraphProps {
	lines?: number;
	align?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonCaptionProps {
	lines?: number;
	align?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonImageProps {
	radius?: string | number;
	aspect?: {
		x: number;
		y: number;
	};
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SkeletonCircleProps {}

export interface SkeletonButtonsProps {
	count?: number;
	type?: 'button' | 'action' | 'icon';
	justify?: SkeletonResponsiveValue<'center' | 'start' | 'end'>;
}

export interface SkeletonResponsiveValue<
	T extends string | number | boolean = string | number | boolean,
> {
	base?: T;
	S?: T;
	M?: T;
	L?: T;
	XL?: T;
	XXL?: T;
}

export type SkeletonTheme = 'light' | 'dark';

export type SkeletonEffect = 'wipe' | 'pulse';

export interface SkeletonProps {
	theme?: SkeletonTheme;
	effect?: SkeletonEffect;
	background?: string;
}

export class Skeleton {
	public props: SkeletonProps;

	constructor(props: SkeletonProps) {
		this.props = props;
	}

	public get theme(): SkeletonTheme {
		return this.props.theme || 'light';
	}

	public get effect(): SkeletonEffect {
		return this.props.effect || 'pulse';
	}

	public get effectClass(): string {
		return `effect-${this.effect}`;
	}

	public parseValue(value: string | number): string {
		return typeof value === 'number' ? `${value}px` : value;
	}

	public wrapper(props: SkeletonWrapperProps): string {
		const {children, className} = props;
		const rootClasses = clsx(className, {
			[styles['theme-light'] || '']: this.theme === 'light',
			[styles['theme-dark'] || '']: this.theme === 'dark',
		});
		return `<div class="${rootClasses}" aria-hidden="true" role="presentation">${children}</div>`;
	}

	public repeat(count: number, fill: string): string {
		return Array(count).fill(fill).join('');
	}

	public heading(props: SkeletonHeadingProps = {}): string {
		const {lines = 1, level = 1, align = {base: 'start'}} = props;
		const rootClasses = clsx(
			styles['heading'],
			style({
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'start',
				gap: '[16px]',
				width: 'full',
				alignItems: {
					default: {
						alignBase: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('s')]: {
						alignS: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('m')]: {
						alignM: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('l')]: {
						alignL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xl')]: {
						alignXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xxl')]: {
						alignXXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
				},
			})({
				alignBase: align.base,
				alignS: align.S,
				alignM: align.M,
				alignL: align.L,
				alignXL: align.XL,
				alignXXL: align.XXL,
			}),
		);
		const lineClasses = clsx(
			styles[this.effectClass],
			style({
				width: 'full',
				height: {
					level: {
						'1': 28,
						'2': 26,
						'3': 24,
						'4': 22,
						'5': 20,
						'6': 18,
					},
				},
				borderRadius: {
					level: {
						'1': '[14px]',
						'2': '[13px]',
						'3': '[12px]',
						'4': '[11px]',
						'5': '[10px]',
						'6': '[9px]',
					},
				},
			})({level: String(level)}),
		);
		return `
			<div class="${rootClasses}">
				${this.repeat(lines, `<div class="${lineClasses}"></div>`)}
			</div>
		`;
	}

	public paragraph(props: SkeletonParagraphProps = {}): string {
		const {lines = 1, align = {base: 'start'}} = props;
		const rootClasses = clsx(
			styles['paragraph'],
			style({
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'start',
				gap: '[16px]',
				width: 'full',
				alignItems: {
					default: {
						alignBase: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('s')]: {
						alignS: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('m')]: {
						alignM: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('l')]: {
						alignL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xl')]: {
						alignXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xxl')]: {
						alignXXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
				},
			})({
				alignBase: align.base,
				alignS: align.S,
				alignM: align.M,
				alignL: align.L,
				alignXL: align.XL,
				alignXXL: align.XXL,
			}),
		);
		const lineClasses = clsx(
			styles[this.effectClass],
			style({
				width: 'full',
				height: 14,
				borderRadius: '[7px]',
			}),
		);
		return `
			<div class="${rootClasses}">
				${this.repeat(lines, `<div class="${lineClasses}"></div>`)}
			</div>
		`;
	}

	public caption(props: SkeletonCaptionProps = {}): string {
		const {lines = 1, align = {base: 'start'}} = props;
		const rootClasses = clsx(
			styles['caption'],
			style({
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'start',
				gap: '[12px]',
				width: 'full',
				alignItems: {
					default: {
						alignBase: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('s')]: {
						alignS: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('m')]: {
						alignM: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('l')]: {
						alignL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xl')]: {
						alignXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xxl')]: {
						alignXXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
				},
			})({
				alignBase: align.base,
				alignS: align.S,
				alignM: align.M,
				alignL: align.L,
				alignXL: align.XL,
				alignXXL: align.XXL,
			}),
		);
		const lineClasses = clsx(
			styles[this.effectClass],
			style({
				width: 'full',
				height: 10,
				borderRadius: '[5px]',
			}),
		);
		return `
			<div class="${rootClasses}">
				${this.repeat(lines, `<div class="${lineClasses}"></div>`)}
			</div>
		`;
	}

	public image(props: SkeletonImageProps = {}): string {
		const {aspect = {x: 1, y: 1}, radius = 12} = props;

		const rootClasses = clsx(
			styles[this.effectClass],
			style({
				width: 'full',
			}),
		);

		const parsedRadius = this.parseValue(radius);

		return `<div class="${rootClasses}" style="aspect-ratio:${aspect.x}/${aspect.y};border-radius:${parsedRadius};"></div>`;
	}

	public circle(_props: SkeletonCircleProps = {}): string {
		const rootClasses = clsx(
			styles[this.effectClass],
			style({
				width: 'full',
				borderRadius: '[50%]',
			}),
		);
		return `<div class="${rootClasses}" style="aspect-ratio:1/1;"></div>`;
	}

	public rect(props: SkeletonRectProps = {}): string {
		const {
			radius = 12,
			width = '100%',
			height = '100%',
			minWidth = 0,
			minHeight = 0,
			maxWidth = '100%',
			maxHeight = '100%',
		} = props;

		const parsedRadius = this.parseValue(radius);
		const parsedWidth = this.parseValue(width);
		const parsedHeight = this.parseValue(height);
		const parsedMinWidth = this.parseValue(minWidth);
		const parsedMinHeight = this.parseValue(minHeight);
		const parsedMaxWidth = this.parseValue(maxWidth);
		const parsedMaxHeight = this.parseValue(maxHeight);

		const rootClasses = clsx(styles[this.effectClass]);

		return `<div class="${rootClasses}" style="width:${parsedWidth};height:${parsedHeight};min-width:${parsedMinWidth};min-height:${parsedMinHeight};max-width:${parsedMaxWidth};max-height:${parsedMaxHeight};border-radius:${parsedRadius}"></div>`;
	}

	public buttons(props: SkeletonButtonsProps = {}): string {
		const {count = 1, type = 'button', justify = {base: 'start'}} = props;
		const rootClasses = clsx(
			styles['buttons'],
			{
				[styles['buttons--type-button'] || '']: type === 'button',
				[styles['buttons--type-action'] || '']: type === 'action',
				[styles['buttons--type-icon'] || '']: type === 'icon',
			},
			style({
				display: 'flex',
				flexDirection: 'row',
				flexWrap: 'wrap',
				alignItems: 'center',
				gap: '[14px]',
				justifyContent: {
					default: {
						justifyBase: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('s')]: {
						justifyS: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('m')]: {
						justifyM: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('l')]: {
						justifyL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xl')]: {
						justifyXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
					[breakpoint('xxl')]: {
						justifyXXL: {
							start: 'start',
							center: 'center',
							end: 'end',
						},
					},
				},
			})({
				justifyBase: justify.base,
				justifyS: justify.S,
				justifyM: justify.M,
				justifyL: justify.L,
				justifyXL: justify.XL,
				justifyXXL: justify.XXL,
			}),
		);
		const buttonClasses = clsx(
			styles[this.effectClass],
			style({
				maxWidth: 'full',
				display: 'inline-flex',
				height: 32,
				width: {
					type: {
						button: 96,
						action: 96,
						icon: 32,
					},
				},
				borderRadius: {
					type: {
						button: '[16px]',
						action: '[4px]',
						icon: '[4px]',
					},
				},
			})({type}),
		);
		return `
			<div class="${rootClasses}">
				${this.repeat(count, `<div class="${buttonClasses}"></div>`)}
			</div>
		`;
	}

	public toString(): string {
		return this.wrapper({
			children:
				'To use the skeleton utility, extend the "Skeleton" class, and override the <code>.toString()</code> method.',
		});
	}
}
